/**
 * Shape-oriented PowerPoint tools: add text box / geometric shapes / lines /
 * images, list shapes, delete shapes, reposition/resize, and z-order.
 *
 * Handlers follow the PowerPoint.run/load/sync conventions documented in
 * pptx-tools.ts. Shape construction goes through ../shape-builders so the
 * primitives and compose_slide share one implementation.
 */

import type { PptxTool } from "../tool-types";
import { getGeneratedImage } from "../generated-images";
import { readChatFileWithInfo } from "../staged-data";
import { recordImageProvenance, type ImageSource } from "../image-provenance";
import {
  buildShape,
  ensureImageFills,
  fitRect,
  normalizeImageToPng,
  fetchImageAsDataUrl,
  shapePlanFromArgs,
  resolveShape,
  GEOMETRIC_SHAPE_TYPES,
  type Rect,
} from "../shape-builders";

/** Load-free helper: locate a freshly added shape's index by id after a sync. */
async function loadShapeIndex(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  shapeId: string
): Promise<number> {
  const shapes = slide.shapes;
  shapes.load("items/id");
  await context.sync();
  return shapes.items.findIndex((s) => s.id === shapeId);
}

export const shapeTools: PptxTool[] = [
  // ─── Add Text Box ─────────────────────────────────────────────────────────

  {
    schema: {
      name: "add_text_box",
      description:
        "Add a text box to a slide at the specified position and size (in points).",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
          text: {
            type: "string",
            description: "Text content for the text box.",
          },
          left: {
            type: "number",
            description: "Horizontal position from the left edge in points. Default: 50.",
          },
          top: {
            type: "number",
            description: "Vertical position from the top edge in points. Default: 50.",
          },
          width: {
            type: "number",
            description: "Width of the text box in points. Default: 400.",
          },
          height: {
            type: "number",
            description: "Height of the text box in points. Default: 100.",
          },
        },
        required: ["index", "text"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);

        const left   = (args.left   as number | undefined) ?? 50;
        const top    = (args.top    as number | undefined) ?? 50;
        const width  = (args.width  as number | undefined) ?? 400;
        const height = (args.height as number | undefined) ?? 100;

        const newShape = slide.shapes.addTextBox(args.text as string, {
          left,
          top,
          width,
          height,
        });
        newShape.load("id");

        await context.sync();

        // Count shapes to return the index
        const shapes = slide.shapes;
        shapes.load("items/id");
        await context.sync();
        const shapeIndex = shapes.items.findIndex((s) => s.id === newShape.id);

        return {
          success: true,
          shapeId: newShape.id,
          shapeIndex,
        };
      });
    },
  },

  // ─── Get Shapes ───────────────────────────────────────────────────────────

  {
    schema: {
      name: "get_shapes",
      description:
        "Get all shapes on a slide with their names, IDs, types, position/size (left, top, width, height in points), and fill colour. Use get_slide for text + font details.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shapes = slide.shapes;
        shapes.load(
          "items/id,items/name,items/type,items/left,items/top,items/width,items/height," +
            "items/fill/type,items/fill/foregroundColor"
        );

        await context.sync();

        return {
          slideIndex: args.index as number,
          shapes: shapes.items.map((shape, i) => ({
            index: i,
            id: shape.id,
            name: shape.name,
            type: shape.type,
            left: shape.left,
            top: shape.top,
            width: shape.width,
            height: shape.height,
            fill: { type: shape.fill.type, color: shape.fill.foregroundColor },
          })),
        };
      });
    },
  },

  // ─── Delete All Shapes ────────────────────────────────────────────────────

  {
    schema: {
      name: "delete_all_shapes",
      description:
        "Delete ALL shapes on a slide in a single operation. Prefer this over calling delete_shape repeatedly — it is far more efficient and avoids index-shifting bugs.",
      parameters: {
        type: "object",
        properties: {
          index: {
            type: "number",
            description: "0-based index of the slide.",
          },
        },
        required: ["index"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shapes = slide.shapes;
        shapes.load("items/id,items/name");
        await context.sync();

        const deletedNames = shapes.items.map((s) => s.name);
        shapes.items.forEach((shape) => shape.delete());
        await context.sync();

        // Reload to confirm 0 shapes remain
        shapes.load("items");
        await context.sync();

        return {
          success: true,
          deletedCount: deletedNames.length,
          deletedShapes: deletedNames,
          remainingShapes: shapes.items.length,
          slideIsEmpty: shapes.items.length === 0,
        };
      });
    },
  },

  // ─── Delete Shape ─────────────────────────────────────────────────────────

  {
    schema: {
      name: "delete_shape",
      description:
        "Delete a shape from a slide. Identify it by shapeId (preferred — use the id returned by get_slide/get_shapes) OR by shapeIndex.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: {
            type: "number",
            description: "0-based index of the slide.",
          },
          shapeId: {
            type: "string",
            description: "ID of the shape (from get_slide / get_shapes). Preferred over shapeIndex.",
          },
          shapeIndex: {
            type: "number",
            description: "0-based index of the shape. Used only when shapeId is not available.",
          },
        },
        required: ["slideIndex"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);

        if (args.shapeId !== undefined) {
          const shapes = slide.shapes;
          shapes.load("items/id");
          await context.sync();

          const found = shapes.items.find((s) => s.id === args.shapeId);
          if (!found) {
            throw new Error(`Shape with id "${args.shapeId}" not found on slide ${args.slideIndex}.`);
          }
          found.delete();
        } else if (args.shapeIndex !== undefined) {
          slide.shapes.getItemAt(args.shapeIndex as number).delete();
        } else {
          throw new Error("Provide either shapeId or shapeIndex.");
        }

        await context.sync();

        return { success: true };
      });
    },
  },

  // ─── Set Shape Position ───────────────────────────────────────────────────

  {
    schema: {
      name: "set_shape_position",
      description:
        "Move and/or resize a shape on a slide by setting its left, top, width, and/or height (in points). Identify the shape by shapeId (preferred — use the id returned by get_slide/get_shapes) OR by shapeIndex. All position/size properties are optional — supply only the ones you want to change.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: {
            type: "number",
            description: "0-based index of the slide.",
          },
          shapeId: {
            type: "string",
            description: "ID of the shape (from get_slide / get_shapes). Preferred over shapeIndex.",
          },
          shapeIndex: {
            type: "number",
            description: "0-based index of the shape. Used only when shapeId is not available.",
          },
          left: {
            type: "number",
            description: "Distance from the left edge of the slide in points.",
          },
          top: {
            type: "number",
            description: "Distance from the top edge of the slide in points.",
          },
          width: {
            type: "number",
            description: "Width of the shape in points.",
          },
          height: {
            type: "number",
            description: "Height of the shape in points.",
          },
        },
        required: ["slideIndex"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);

        let shape: PowerPoint.Shape;

        if (args.shapeId !== undefined) {
          // Locate shape by ID — load all shapes and find the match
          const shapes = slide.shapes;
          shapes.load("items/id");
          await context.sync();

          const found = shapes.items.find((s) => s.id === args.shapeId);
          if (!found) {
            throw new Error(`Shape with id "${args.shapeId}" not found on slide ${args.slideIndex}.`);
          }
          shape = found;
        } else if (args.shapeIndex !== undefined) {
          shape = slide.shapes.getItemAt(args.shapeIndex as number);
        } else {
          throw new Error("Provide either shapeId or shapeIndex.");
        }

        shape.load("left,top,width,height");
        await context.sync();

        if (args.left !== undefined) shape.left = args.left as number;
        if (args.top !== undefined) shape.top = args.top as number;
        if (args.width !== undefined) shape.width = args.width as number;
        if (args.height !== undefined) shape.height = args.height as number;

        await context.sync();

        return {
          success: true,
          left: shape.left,
          top: shape.top,
          width: shape.width,
          height: shape.height,
        };
      });
    },
  },

  // ─── Add Shape (geometric) ────────────────────────────────────────────────

  {
    // Geometric shapes + fill/line formatting are PowerPointApi 1.4.
    minRequirementSet: "1.4",
    schema: {
      name: "add_shape",
      description:
        "Add a geometric shape (rectangle, ellipse, arrow, chevron…) to a slide with optional " +
        "solid fill, border, and centred text. Use this for design elements: colour panels, " +
        "cards, dividers, icon stand-ins. Position/size in points on the 960×540 canvas.",
      parameters: {
        type: "object",
        properties: {
          index: { type: "number", description: "0-based index of the slide." },
          shapeType: {
            type: "string",
            description: `Shape type. One of: ${GEOMETRIC_SHAPE_TYPES.join(", ")}.`,
          },
          left: { type: "number", description: "Left position in points." },
          top: { type: "number", description: "Top position in points." },
          width: { type: "number", description: "Width in points." },
          height: { type: "number", description: "Height in points." },
          fillColor: { type: "string", description: 'Solid fill colour, hex "#RRGGBB".' },
          fillTransparency: {
            type: "number",
            description: "Fill transparency 0.0 (opaque) – 1.0 (clear).",
          },
          lineColor: { type: "string", description: 'Border colour, hex "#RRGGBB".' },
          lineWeight: { type: "number", description: "Border weight in points." },
          text: { type: "string", description: "Optional text inside the shape." },
          fontName: { type: "string", description: "Font family for the text." },
          fontSize: { type: "number", description: "Font size in points." },
          fontColor: { type: "string", description: 'Text colour, hex "#RRGGBB".' },
          bold: { type: "boolean" },
          italic: { type: "boolean" },
        },
        required: ["index", "shapeType", "left", "top", "width", "height"],
      },
    },
    handler: async (args) => {
      // shapePlanFromArgs validates shapeType (throws before any Office work).
      const plan = shapePlanFromArgs(args);
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shape = buildShape(slide, plan);
        shape.load("id");
        await context.sync();

        const shapeIndex = await loadShapeIndex(context, slide, shape.id);
        return { success: true, shapeId: shape.id, shapeIndex };
      });
    },
  },

  // ─── Add Line ─────────────────────────────────────────────────────────────

  {
    minRequirementSet: "1.4",
    schema: {
      name: "add_line",
      description:
        "Add a straight line to a slide, drawn corner-to-corner of its bounding box " +
        "(left/top → left+width/top+height, in points). Use for dividers and accents; " +
        "for a horizontal rule use height 0.",
      parameters: {
        type: "object",
        properties: {
          index: { type: "number", description: "0-based index of the slide." },
          left: { type: "number", description: "Bounding-box left in points." },
          top: { type: "number", description: "Bounding-box top in points." },
          width: { type: "number", description: "Bounding-box width in points." },
          height: { type: "number", description: "Bounding-box height in points (0 = horizontal)." },
          color: { type: "string", description: 'Line colour, hex "#RRGGBB".' },
          weight: { type: "number", description: "Line weight in points (default host weight)." },
        },
        required: ["index", "left", "top", "width", "height"],
      },
    },
    handler: async (args) => {
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shape = buildShape(slide, {
          kind: "line",
          rect: {
            left: args.left as number,
            top: args.top as number,
            width: args.width as number,
            height: args.height as number,
          },
          color: typeof args.color === "string" ? args.color : undefined,
          weight: typeof args.weight === "number" ? args.weight : undefined,
        });
        shape.load("id");
        await context.sync();

        const shapeIndex = await loadShapeIndex(context, slide, shape.id);
        return { success: true, shapeId: shape.id, shapeIndex };
      });
    },
  },

  // ─── Insert Image ─────────────────────────────────────────────────────────

  {
    // ShapeFill.setImage is PowerPointApi 1.8. Deliberately no Common-API
    // setSelectedDataAsync fallback: that inserts on whichever slide is
    // *in view* — nondeterministic mid-build. Gating off is better UX than an
    // image landing on the wrong slide.
    minRequirementSet: "1.8",
    schema: {
      name: "insert_image",
      description:
        "Insert an image onto a slide at the given position/size (points). Provide ONE source: " +
        "useGeneratedImage: true (an image generated in this chat — the add-in already holds " +
        "its bytes, ALWAYS prefer this for generated images; never ask the user to download " +
        "anything), base64 (raw or data-URL), or url (publicly accessible with CORS). By default " +
        "the image keeps its aspect ratio inside the given rect (fit: contain); fit: stretch " +
        "fills the rect exactly. The image is rendered as a picture-filled rectangle — it can " +
        "be moved/resized like any shape but has no crop.",
      parameters: {
        type: "object",
        properties: {
          index: { type: "number", description: "0-based index of the slide." },
          useGeneratedImage: {
            type: "boolean",
            description:
              "Use the most recent image generated in this chat session (the add-in caches it automatically).",
          },
          generatedFileId: {
            type: "string",
            description: "Optional: pick a specific generated image by its fileId instead of the latest.",
          },
          useUploadedImage: {
            type: "string",
            description:
              "Use a file from this chat (paperclip upload — fileName or alias, see list_uploaded_files), the attached local folder (path), or a Usable workspace (fileId from search-files).",
          },
          base64: { type: "string", description: "Image data as base64 or data-URL." },
          url: { type: "string", description: "Publicly accessible image URL (CORS required)." },
          left: { type: "number", description: "Target-rect left in points." },
          top: { type: "number", description: "Target-rect top in points." },
          width: { type: "number", description: "Target-rect width in points." },
          height: { type: "number", description: "Target-rect height in points." },
          fit: {
            type: "string",
            description: '"contain" (default — preserve aspect ratio inside the rect) or "stretch".',
          },
        },
        required: ["index", "left", "top", "width", "height"],
      },
    },
    handler: async (args) => {
      const useGenerated = args.useGeneratedImage === true || typeof args.generatedFileId === "string";
      const useUploaded = typeof args.useUploadedImage === "string" && (args.useUploadedImage as string).length > 0;
      const hasBase64 = typeof args.base64 === "string" && (args.base64 as string).length > 0;
      const hasUrl = typeof args.url === "string" && (args.url as string).length > 0;
      const sources = [useGenerated, useUploaded, hasBase64, hasUrl].filter(Boolean).length;
      if (sources !== 1) {
        throw new Error(
          "Provide exactly one image source: useGeneratedImage, useUploadedImage, base64, or url."
        );
      }

      // Resolve OUTSIDE PowerPoint.run (network + decode are browser work),
      // then NORMALIZE to clean PNG: AI-generated images arrive as WebP or
      // line-wrapped base64, which PowerPoint renders as "The picture can't
      // be displayed" even though fill.setImage accepts the call.
      let rawInput: string;
      let provenance: { source: ImageSource; ref?: string };
      if (useUploaded) {
        const resolved = await readChatFileWithInfo(args.useUploadedImage as string);
        rawInput = resolved.dataUrl;
        provenance = { source: resolved.source, ref: resolved.ref };
      } else if (useGenerated) {
        const cached = getGeneratedImage(
          typeof args.generatedFileId === "string" ? (args.generatedFileId as string) : undefined
        );
        if (!cached) {
          throw new Error(
            "No generated image is cached for this session. Generate an image first (it is " +
              "pushed to the add-in automatically), then call insert_image with useGeneratedImage: true."
          );
        }
        rawInput = `data:${cached.mimeType};base64,${cached.base64}`;
        provenance = { source: "generated", ref: cached.fileId };
      } else if (hasUrl) {
        rawInput = await fetchImageAsDataUrl(args.url as string);
        provenance = { source: "url", ref: args.url as string };
      } else {
        rawInput = args.base64 as string;
        provenance = { source: "base64" };
      }
      const normalized = await normalizeImageToPng(rawInput);
      if (!normalized) {
        throw new Error(
          "The image data could not be decoded — it may be corrupt or an unsupported format. " +
            "Provide a PNG or JPEG as base64 (or a CORS-accessible URL to one)."
        );
      }

      const target: Rect = {
        left: args.left as number,
        top: args.top as number,
        width: args.width as number,
        height: args.height as number,
      };
      const fit = args.fit === "stretch" ? "stretch" : "contain";
      const rect = fitRect(normalized, target, fit);

      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.index as number);
        const shape = buildShape(slide, { kind: "image", rect, base64: normalized.base64 });
        shape.load("id");
        await context.sync();

        // Verify the picture fill rendered (fill.setImage can silently not stick).
        const imageFills = await ensureImageFills(context, [{ shape, base64: normalized.base64 }]);
        if (imageFills.failed > 0) {
          throw new Error(
            "The image fill did not apply even after a retry — the shape was created but renders empty. " +
              "Delete the shape and try again (possibly with a smaller image)."
          );
        }

        recordImageProvenance({
          scope: args.index as number,
          shapeId: shape.id,
          source: provenance.source,
          ref: provenance.ref,
          base64: normalized.base64,
        });

        const shapeIndex = await loadShapeIndex(context, slide, shape.id);
        return {
          success: true,
          shapeId: shape.id,
          shapeIndex,
          rect,
          fitApplied: fit,
          normalizedTo: `${normalized.width}×${normalized.height}px PNG`,
        };
      });
    },
  },

  // ─── Set Z-Order ──────────────────────────────────────────────────────────

  {
    // Shape.setZOrder is PowerPointApi 1.8.
    minRequirementSet: "1.8",
    schema: {
      name: "set_z_order",
      description:
        "Change a shape's stacking order: bring it in front of or behind other shapes. " +
        "Identify the shape by shapeId (preferred) or shapeIndex.",
      parameters: {
        type: "object",
        properties: {
          slideIndex: { type: "number", description: "0-based index of the slide." },
          shapeId: { type: "string", description: "ID of the shape (from get_slide/get_shapes)." },
          shapeIndex: { type: "number", description: "0-based shape index when no id is available." },
          position: {
            type: "string",
            description: 'One of "bringToFront", "sendToBack", "bringForward", "sendBackward".',
          },
        },
        required: ["slideIndex", "position"],
      },
    },
    handler: async (args) => {
      const zOrderMap: Record<string, string> = {
        bringToFront: "BringToFront",
        sendToBack: "SendToBack",
        bringForward: "BringForward",
        sendBackward: "SendBackward",
      };
      const mapped = zOrderMap[String(args.position)];
      if (!mapped) {
        throw new Error(
          `Unknown position "${String(args.position)}". Use bringToFront, sendToBack, bringForward, or sendBackward.`
        );
      }
      return PowerPoint.run(async (context) => {
        const slide = context.presentation.slides.getItemAt(args.slideIndex as number);
        const shapes = slide.shapes;
        shapes.load("items/id");
        await context.sync();

        const shape = resolveShape(shapes, args);
        shape.setZOrder(mapped as unknown as PowerPoint.ShapeZOrder);
        await context.sync();

        return { success: true };
      });
    },
  },
];
